import { Button, ProgressBar, utils } from '@makeship/core';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import posthog from 'posthog-js';
import { Element as ScrollElement } from 'react-scroll';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import config from '../../../config.json';
import { useStore } from '../../store';
import { addProduct, showCart } from '../../store/store.actions';
import {
  productMOQ,
  ProductStage,
  ProductTag,
  CartEventLocations,
  BadgeText,
  StretchGoalsTheme,
} from '../../types/common';
import {
  analyticsTrackAddToCart,
  analyticsTrackItemSelectedFromList,
  analyticsTrackViewCart,
  isUserLoggedIn,
  trackSubscribeButtonClicked,
} from '../../utils/analytics';
import {
  getBadgeText,
  getStage,
  hasSale,
  parseMetafields,
  hasProductTag,
  getProductIDFromShopifyGID,
  calculateTotalInventorySold,
  getEvergreenInventoryState,
  isProductSoldOut,
  shouldShowProgressBar,
  isEligibleForTimer,
  getProgressBarProps,
  isApparelProduct,
  isCurrentDateBeforeGivenMetafieldDate,
  isHideUnitsVariant,
} from '../../utils/product';
import Badge from '../Badge';
import Countdown from '../Countdown';
import { Caption, CaptionStyles, Overline, P2Styles, S2, S3, S3Styles } from '../Typography';
import SpamFlaggingContext from '../../context/spamFlagging';
import { formatNumberStringWithComma } from '../../utils/accounting';
import { shouldDisplayBadge, fetchCreatorCollectionDetails } from '../../utils/collections';
import { getPetitionPledgeCountByExternalProductID } from '../../api/storefront/petition';
import { CookieConsent } from '../../types/cookies';
import { isHolidayHideawayProduct } from '../../utils/bfcm';
import { ResultType } from '../Header';
import { experimentKeys } from '../../utils/flagging';
import { ExperimentVariant, getExperimentFlags } from '../../context/flagging';

const LazySubscribeModal = dynamic(() => import('../Modal/SubscribeModal'), { ssr: false });

const { elements } = config;

type ProductCardProps = {
  product: Shopify.Product;
  isOnMainPage?: boolean;
  isOnVinylCollectionPage?: boolean;
  titleForTracking?: string;
  query?: string;
  isOnCreatorCollectionPage?: boolean;
  isCreatorBranded?: boolean;
};

type BadgesContent = {
  [key: string]: BadgeText;
};

const badgesContent: BadgesContent = {
  [ProductStage.ComingSoon]: BadgeText.ComingSoon,
  [ProductStage.Past]: BadgeText.SoldOut,
  [ProductStage.PetitionSuccess]: BadgeText.PetitionSuccess,
};

const ButtonStyles = css`
  bottom: 0;
  left: 0;
  width: 100%;
  height: 52px;
  margin-top: 8px;
`;

const AddToCartButton = styled(Button.Primary)<{ isCreatorBranded?: boolean; isOnCreatorCollectionPage?: boolean }>`
  ${ButtonStyles}
  margin-top: auto;
  align-self: flex-end;

  ${({ isCreatorBranded, theme }) =>
    isCreatorBranded &&
    `
      background: ${theme.colors.neutral7};
      &:hover {
        background: ${theme.colors.neutral6};
      }
    `}

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${({ isOnCreatorCollectionPage }) => !isOnCreatorCollectionPage && `height: 38px;`}
  }
`;

const SelectSizeLink = styled(Button.Tertiary)<{ isCreatorBranded?: boolean; isOnCreatorCollectionPage?: boolean }>`
  ${ButtonStyles}

  ${({ isCreatorBranded, theme }) =>
    isCreatorBranded &&
    `
      background: ${theme.colors.neutral1};
      color: ${theme.colors.neutral7};
      border: 1px solid ${theme.colors.neutral7};
      &:hover {
        background: linear-gradient(90deg, ${theme.colors.neutral1} 0%, #E3E3E3 100%);
      }
    `}

      @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${({ isOnCreatorCollectionPage }) => !isOnCreatorCollectionPage && `height: 38px;`}
  }
`;

const ImageWrapper = styled.div<{ hide?: boolean; isPetition: boolean }>`
  width: 100%;
  margin-bottom: ${({ isPetition }) => (isPetition ? `2px` : `12px`)};
  padding: 0px;
`;

const Container = styled.div<{ isOnCreatorCollectionPage?: boolean; shouldShowFreeGiftStretchGoalBadge: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;
  padding: ${({ isOnCreatorCollectionPage }) => (isOnCreatorCollectionPage ? `10px 6px` : `10px`)};
  text-align: left;
  ${({ isOnCreatorCollectionPage, shouldShowFreeGiftStretchGoalBadge }) =>
    isOnCreatorCollectionPage && shouldShowFreeGiftStretchGoalBadge && `margin-bottom: 10px;`}

  &:hover {
    ${ImageWrapper} {
      padding: 0;
    }
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 4px;
`;

const LinkWrapper = styled.div`
  margin-bottom: 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    margin-bottom: 16px;
  } ;
`;

const AddDots = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Title = styled(S2)<{ invertColor: boolean | undefined }>`
  ${AddDots}
  ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${S3Styles}
    ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};
  }
`;

const PetitionTitle = styled(S2)`
  color: ${({ theme }) => `${theme.colors.prelaunchPurple}`};
`;

const ProductDetailWrapper = styled.div<{
  invertColor: boolean | undefined;
  isComingSoon?: boolean;
  isPetition?: boolean;
}>`
  ${P2Styles}
  ${AddDots}
  margin: ${({ isComingSoon, isPetition }) => (isComingSoon ? `0 0 8px 0` : isPetition ? `0 0 6px 0` : `0 0 24px 0`)};
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
  ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${CaptionStyles}
    ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};
  }
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Stats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const StatsText = styled(Caption)<{ invertColor: boolean | undefined }>`
  ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    margin-bottom: 2px;
  }
`;

const DateText = styled(S3)<{ invertColor: boolean | undefined }>`
  ${({ invertColor, theme }) => (invertColor ? `color: ${theme.colors.neutral1};` : ``)};
`;

const BlueImage = styled(Image)`
  filter: invert(10%) sepia(90%) saturate(5268%) hue-rotate(270deg) brightness(65%) contrast(155%);
`;

const StyledBadge = styled(Badge)`
  position: absolute;
  top: 0%;
  right: 2%;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    top: 1.5%;
    right: 2.5%;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    top: 1%;
    right: 4.5%;
  }
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ShippingAvailability = styled(S2)`
  color: ${({ theme }) => theme.colors.primary};
`;

const ShippingAvailabilityWrapper = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const StyledImage = styled(Image)<{ isPetition?: boolean; isOnCreatorBrandedPage?: boolean }>`
  border-radius: 0px 8px 8px 8px;
  ${({ isPetition, theme, isOnCreatorBrandedPage }) =>
    !isPetition &&
    !isOnCreatorBrandedPage &&
    `
    background-color: ${utils.hexToRGBA(theme.colors.cloud, theme.alpha.light)};
  `}
`;

const StyledImageBorder = styled.div<{ isPetition?: boolean }>`
  display: flex;
  border-radius: 0px 8px 8px 8px;

  ${({ isPetition, theme }) =>
    !isPetition &&
    `
      border: 1px solid ${utils.hexToRGBA(theme.colors.midnight, theme.alpha.extraLight)};
    `}
`;

const BringItBackButton = styled(Button.Secondary)<{ isCreatorBranded?: boolean; isOnCreatorCollectionPage?: boolean }>`
  ${ButtonStyles}
  margin-top: auto;
  ${({ isCreatorBranded, theme }) =>
    isCreatorBranded &&
    `
      background: ${theme.colors.neutral1};
      color: ${theme.colors.neutral7};
      border: 1px solid ${theme.colors.neutral7};
      &:hover {
        background: linear-gradient(90deg, ${theme.colors.neutral1} 0%, #E3E3E3 100%);
      }
    `}
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    ${({ isOnCreatorCollectionPage }) => !isOnCreatorCollectionPage && `height: 38px;`}
  }
`;

const BaseRewardPill = styled.div`
  position: absolute;
  top: 4%;
  left: 7px;
  border-radius: 45px;
  padding: 6px 10px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    top: 4%;
    left: 4px;
  }
`;

const RewardPill = styled(BaseRewardPill)<{ stretchGoalsTheme: string; isOnCreatorCollectionPage?: boolean }>`
  background: ${({ stretchGoalsTheme }) => getPillBackgroundGradient(stretchGoalsTheme)};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    top: ${({ isOnCreatorCollectionPage }) => (!isOnCreatorCollectionPage ? '0%' : '-1%')};
  }
  top: 1%;
`;

const RewardText = styled(Overline)`
  color: ${({ theme }) => theme.colors.makeshipBlack};
  font-weight: bold;
`;

const Hyperlink = styled.a``;

const getPillBackgroundGradient = (stretchGoalsTheme: string) => {
  switch (stretchGoalsTheme) {
    case StretchGoalsTheme.Titanium:
      return 'linear-gradient(95deg, #578DC7 0%, #80B1DD 22%, #ACD0EB 44%, #D3E3F2 64%, #84B1DF 82%, #ADCFEB 100%)';
    case StretchGoalsTheme.Silver:
      return 'linear-gradient(95deg, #828488 0%, #DDE0E8 22%, #CDCDCF 44%, #A3A4AA 64%, #F1F0F5 82%, #E4E4E4 100%)';
    case StretchGoalsTheme.Gold:
    default:
      return 'linear-gradient(95deg, #9a7f36 0%, #9c7d37 22%, #bc9d41 44%, #fdfbbc 64%, #dfc358 82%, #9d803c 100%);';
  }
};

const renderDateStats = (
  stage: string,
  invertColor: boolean | undefined,
  { enddate, launch, petitionEndDate }: Metafields,
): React.ReactNode => {
  if (stage === 'comingsoon' && launch) {
    return (
      <CountdownWrapper>
        <IconWrapper>🗓️</IconWrapper>
        <DateText invertColor={invertColor}>
          {launch && utils.parseDate(launch).toDate().toLocaleString('default', { month: 'long', day: 'numeric' })}
        </DateText>
      </CountdownWrapper>
    );
  }
  if (stage === 'petition' && petitionEndDate && utils.getDayDiffFrom(petitionEndDate) >= 0) {
    return (
      <CountdownWrapper>
        <IconWrapper>🕓</IconWrapper>
        <DateText data-testid="product-card-date-text" invertColor={invertColor}>
          <Countdown date={petitionEndDate} />
        </DateText>
      </CountdownWrapper>
    );
  }
  if (stage === ProductStage.PetitionSuccess) {
    return <></>;
  }
  if (enddate && utils.getDayDiffFrom(enddate) >= 0) {
    return (
      <CountdownWrapper>
        <IconWrapper>🕓</IconWrapper>
        <DateText data-testid="product-card-date-text" invertColor={invertColor}>
          <Countdown date={enddate} />
        </DateText>
      </CountdownWrapper>
    );
  }
  return <></>;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  isOnMainPage,
  isOnVinylCollectionPage,
  titleForTracking,
  query,
  isOnCreatorCollectionPage,
  isCreatorBranded,
}: ProductCardProps) => {
  const { state, dispatch } = useStore();

  const { incrementSpamCounter, isSpam } = useContext(SpamFlaggingContext);

  const [totalSold, setTotalSold] = React.useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasAchievedStretchGoals, setHasAchievedStretchGoals] = useState(false);
  const [shouldEnableStretchGoals, setShouldEnableStretchGoals] = useState(false);
  const [stretchGoalsTheme, setStretchGoalsTheme] = useState('');

  const metafields = parseMetafields(product.metafields);
  const stage = getStage(product.tags);
  const sale = hasSale(product);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isPetition = hasProductTag(product.tags, ProductTag.Petition);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const isLiveWithStretchGoalsTag = hasProductTag(product.tags, ProductTag.StretchGoals) && stage === ProductStage.Live;
  const isSuccessfulPetitionNotLive = stage === ProductStage.PetitionSuccess;
  const inventoryState = getEvergreenInventoryState(product);
  const moq = Number(metafields.moq) || productMOQ[product.productType] || config.defaultMOQ;
  const isSoldOut = isProductSoldOut(product, totalSold);
  const invertColor = isOnMainPage && stage === ProductStage.ComingSoon;
  const badgeText = getBadgeText(sale, stage, inventoryState) || badgesContent[stage];
  const hasManyVariants = product.variants.edges.length > 1;
  const isApparel = isApparelProduct(product);

  const shouldShowFreeGiftStretchGoalBadge = isLiveWithStretchGoalsTag && shouldEnableStretchGoals;

  const { asPath } = useRouter();

  const cardRef = useRef(null);
  const hideUnits = hasProductTag(product.tags, ProductTag.HideUnits) || isHideUnitsVariant(product);

  const getStatsText = (): React.ReactNode => {
    if (isLimited) {
      if (totalSold < 0) {
        return `${formatNumberStringWithComma(moq)}/${formatNumberStringWithComma(moq)} Sold`;
      }
      return `${formatNumberStringWithComma(moq - totalSold)}/${formatNumberStringWithComma(moq)} Sold`;
    }
    if (hideUnits && totalSold >= moq) {
      return `100%+ Funded`;
    }
    if (isPetition || isSuccessfulPetitionNotLive) {
      return `${formatNumberStringWithComma(Math.floor((totalSold / moq) * 100))}% of goal`;
    }

    return `${formatNumberStringWithComma(Math.floor((totalSold / moq) * 100))}% Funded`;
  };

  const setVisitedCard = () => {
    const cookieConsentAccepted = Cookies.get(CookieConsent.Functional) === 'true';
    if (cookieConsentAccepted) {
      sessionStorage.setItem('visitedProductCard', product.title);
    }
  };

  const handleVisible = async (product: Shopify.Product) => {
    const shopifyProductInventory = Math.abs(product.totalInventory ?? 0);
    if (isPetition || isSuccessfulPetitionNotLive) {
      const pledgeCount = await getPetitionPledgeCountByExternalProductID(getProductIDFromShopifyGID(product.id))
        .then((response) => response.data)
        .catch(() => shopifyProductInventory);
      setTotalSold(pledgeCount ?? shopifyProductInventory);
    } else {
      const currentTotalSold = await calculateTotalInventorySold(product);
      setTotalSold(currentTotalSold ?? shopifyProductInventory);
    }
  };

  const isOnProductCard = true;
  const { isGradient, percentage, funded, height } = getProgressBarProps(product, isOnProductCard, totalSold);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleVisible(product);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '20%',
      },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [product.id]);

  useEffect(() => {
    if (isLiveWithStretchGoalsTag) {
      (async () => {
        const {
          shouldEnableStretchGoals,
          goalsAchieved,
          productDropCollectionMetafields,
        } = await fetchCreatorCollectionDetails(product);
        setShouldEnableStretchGoals(shouldEnableStretchGoals);
        setHasAchievedStretchGoals(goalsAchieved);
        setStretchGoalsTheme(productDropCollectionMetafields?.stretchGoalsTheme || '');
      })();
    }
  }, [isLiveWithStretchGoalsTag, product]);

  const handleProductCardClick = () => {
    setVisitedCard();
    productCardTrackingClick();
  };

  const productCardTrackingClick = () => {
    posthog.capture('product_card_click', {
      product_title: product.title,
      product_id: getProductIDFromShopifyGID(product.id),
      product_stage: stage,
      product_tags: product.tags,
      location: asPath,
      product_type: product.productType,
    });
  };

  const handleAddToCart = () => {
    addProduct(state.cart, 1, product.variants.edges[0]?.node).then((action) => dispatch(action));
    dispatch(showCart());
    incrementSpamCounter();
    if (!isSpam) {
      analyticsTrackAddToCart(
        product,
        1,
        state.user,
        CartEventLocations.ProductThumbnail,
        product.variants.edges[0]?.node,
        metafields.campaignID,
      );
      analyticsTrackViewCart(
        state.cart.shopifyCart?.lines.edges,
        state.cart.shopifyCart?.cost.subtotalAmount.amount,
        state.user,
        CartEventLocations.ProductThumbnail,
      );
    }
  };

  const handleSelectSizeButtonClick = () => {
    posthog.capture('product_card_select_size_click', {
      product_title: product.title,
      product_id: getProductIDFromShopifyGID(product.id),
      product_stage: stage,
      product_tags: product.tags,
      location: asPath,
      is_logged_in: isUserLoggedIn(state.user),
    });
  };

  const renderProductCardButton = (product: Shopify.Product, stage: ProductStage) => {
    if (stage === ProductStage.Live && product.availableForSale && !isSoldOut) {
      if (!hasManyVariants) {
        return (
          <AddToCartButton
            isCreatorBranded={isCreatorBranded}
            isOnCreatorCollectionPage={isOnCreatorCollectionPage}
            id={elements.addToCartButton}
            data-testid="add-to-cart-button"
            onClick={handleAddToCart}
          >
            Add to Cart
          </AddToCartButton>
        );
      }
      if (isApparel) {
        return (
          <Link
            href={{
              pathname: `/products/${product.handle}`,
              query: { scrollToSizeSelector: true },
            }}
            passHref
          >
            <SelectSizeLink
              isCreatorBranded={isCreatorBranded}
              isOnCreatorCollectionPage={isOnCreatorCollectionPage}
              as="a"
              id="select-size-button"
              onClick={handleSelectSizeButtonClick}
            >
              Select Size
            </SelectSizeLink>
          </Link>
        );
      }
    }
    if (isOnCreatorCollectionPage && stage === ProductStage.Past) {
      return (
        <>
          <BringItBackButton
            isCreatorBranded={isCreatorBranded}
            isOnCreatorCollectionPage={isOnCreatorCollectionPage}
            onClick={() => {
              setIsModalOpen(true);
              trackSubscribeButtonClicked(product, state.user, 'Bring it Back!');
            }}
          >
            Bring it Back!
          </BringItBackButton>{' '}
          <LazySubscribeModal
            isOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            tags={`subscribed-product, ${product.title}, ${metafields.creator}, bring-it-back`}
            description={`Let ${metafields.creator} know you want to bring it back!`}
            title="Want to see this product return?"
            ctaText="Bring it Back!"
            product={product}
          />
        </>
      );
    }
    return null;
  };

  return (
    <Container
      data-gtm-name="product-card-container"
      ref={cardRef}
      onClick={() => {
        titleForTracking === 'search' &&
          posthog.capture('search_result_click', {
            product_title: product.title,
            product_id: getProductIDFromShopifyGID(product.id),
            search_query: query?.trim(),
            location: 'search_results_page',
            product_stage: stage,
            product_tags: product.tags,
            is_logged_in: isUserLoggedIn(state.user),
            result_type: ResultType.Product,
          });
        analyticsTrackItemSelectedFromList(product, titleForTracking, state.user);
      }}
      isOnCreatorCollectionPage={isOnCreatorCollectionPage}
      shouldShowFreeGiftStretchGoalBadge={shouldShowFreeGiftStretchGoalBadge}
    >
      <div>
        <ImageWrapper hide={stage === ProductStage.ComingSoon} isPetition={isPetition}>
          <Link href={`/products/${product.handle}`} passHref>
            <Hyperlink>
              <LinkWrapper tabIndex={0} data-gtm-name="product-card-anchor" onClick={handleProductCardClick}>
                {!invertColor ? (
                  <StyledImageBorder isPetition={isPetition}>
                    <StyledImage
                      src={product.images.edges[0]?.node.transformedSrc || '/assets/default_product_asset.png'}
                      alt={product.images.edges[0]?.node.altText || 'product image'}
                      width={500}
                      height={500}
                      quality={90}
                      unoptimized
                      data-testid="product-card-image"
                      isPetition={isPetition}
                      isOnCreatorBrandedPage={isCreatorBranded}
                    />
                  </StyledImageBorder>
                ) : (
                  <BlueImage
                    src={product.images.edges[0]?.node.transformedSrc || '/assets/default_product_asset.png'}
                    alt={product.images.edges[0]?.node.altText || 'product image'}
                    width={500}
                    height={500}
                    quality={90}
                    unoptimized
                    data-testid="product-card-image"
                  />
                )}

                {!isOnMainPage && stage !== ProductStage.ComingSoon && !isOnVinylCollectionPage && !isHybrid && (
                  <StyledBadge text={badgeText} isActive={shouldDisplayBadge(stage, sale, inventoryState)} />
                )}
                {shouldShowFreeGiftStretchGoalBadge && (
                  <RewardPill
                    stretchGoalsTheme={stretchGoalsTheme}
                    isOnCreatorCollectionPage={isOnCreatorCollectionPage}
                  >
                    <RewardText>{hasAchievedStretchGoals ? '⭐️ Free Gift!' : '⭐️ Unlock Gift!'}</RewardText>
                  </RewardPill>
                )}
              </LinkWrapper>
            </Hyperlink>
          </Link>
          <ScrollElement name={product.title} />
        </ImageWrapper>
        <Link href={`/products/${product.handle}`} passHref>
          <Hyperlink>
            <LinkWrapper tabIndex={0} data-gtm-name="product-card-anchor" onClick={handleProductCardClick}>
              {isPetition && <PetitionTitle>🚀 Petition</PetitionTitle>}
              <Title data-testid="product-card-title" invertColor={invertColor}>
                {product.title}
              </Title>
              {stage !== ProductStage.Demo && (
                <>
                  <ProductDetailWrapper
                    invertColor={invertColor}
                    isComingSoon={stage === ProductStage.ComingSoon}
                    isPetition={isPetition}
                  >
                    {metafields.creator ? `By ${metafields.creator}` : ''}
                    {/* GITD 2024 */}
                    {(isEvergreen || isHolidayHideawayProduct(product.id)) &&
                      !isSoldOut &&
                      (!isHybrid || !isCurrentDateBeforeGivenMetafieldDate(metafields.delivery)) && (
                        <ShippingAvailabilityWrapper>
                          📦
                          <ShippingAvailability>Ready-to-ship!</ShippingAvailability>
                        </ShippingAvailabilityWrapper>
                      )}
                  </ProductDetailWrapper>
                  {/* GITD 2024 */}
                  {shouldShowProgressBar(stage, product.id, isEvergreen) && (
                    <StatsWrapper data-testid="product-card-funded-bar-wrapper">
                      <ProgressBar isGradient={isGradient} percentage={percentage} funded={funded} height={height} />
                      <Stats>
                        <StatsText data-testid="product-card-stats-text" invertColor={invertColor}>
                          {getStatsText()}
                        </StatsText>
                        {isEligibleForTimer(product) && renderDateStats(stage, invertColor, metafields)}
                      </Stats>
                    </StatsWrapper>
                  )}
                  {(stage === ProductStage.ComingSoon || stage === ProductStage.Failed) && (
                    <>{isEligibleForTimer(product) && renderDateStats(stage, invertColor, metafields)}</>
                  )}
                </>
              )}
            </LinkWrapper>
          </Hyperlink>
        </Link>
      </div>
      {renderProductCardButton(product, stage)}
    </Container>
  );
};

export default ProductCard;
